import { IFormField, DocumentInfoV2 } from "../web-form.objects";
import _ from "lodash";
import { JsonItem } from "../../../entities/pick-list/pick-list.entities";
import { plainToClass } from "class-transformer";

export class FileUploadInputOptions {
    rootFolderPath: string;
    isMultiple: boolean = false; // can upload multiple files
    isPdf: boolean = false; // required to be PDF file

    constructor(rootFolderPath: string, isMultiple: boolean = false, isPdf: boolean = false) {
        this.rootFolderPath = rootFolderPath;
        this.isMultiple = isMultiple;
        this.isPdf = isPdf;
    }
}

export class FileUploadInputField implements IFormField {
    fieldId: string; 
    displayName: string; 
    isRequired: boolean;
    description: string;
    propertyName: string; // treated as the root folder path (Just use dot notation with '/' instead of dots)
    defaultVal: any; 
    initialVal: any;

    options: FileUploadInputOptions;

    isMultiple: boolean; // used to identify if multiple items are being edited
    maxFileSize: number = 100000000; // 100 MB 

    getFilesRoute: string = "/api/upload-service/get-files";
    addFilesRoute: string = "/api/upload-service/upload-file";
    removeFilesRoute: string = "/api/upload-service/delete-file";
    downloadFileRoute: string = "/api/upload-service/download-file";

    storedValues: string[]; // Stored UUID strings
    currentDocuments: DocumentInfoV2[];
    filesToUpload: File[];

    fileContainerId: string;
    fileLoadingId: string;
    fileUploadBlockId: string;
    fileUploadInputId: string;
    fileGalleryId: string;
    fileErrorBlock: string;

    constructor(propertyName: string, fieldId: string, displayName: string, description: string, isRequired: boolean, options: FileUploadInputOptions) {
        this.fieldId = fieldId;
        this.displayName = displayName;
        this.description = description;
        this.isRequired = isRequired;
        this.propertyName = propertyName; 
        this.options = options;

        this.storedValues = [];
        this.defaultVal = "";
        this.initialVal = "";

        this.isMultiple = false;

        this.filesToUpload = [];

        this.fileContainerId = `#${this.fieldId}-file-upload-container`;
        this.fileLoadingId = `#${this.fieldId}-loading-container`;
        this.fileUploadBlockId = `#${this.fieldId}-file-upload-block`;
        this.fileUploadInputId = `#${this.fieldId}-input`;
        this.fileGalleryId = `#${this.fieldId}-gallery`;
        this.fileErrorBlock = `#${this.fieldId}-error-block`;
    }

    private setHtml(): void {
        let loadingBlock = `
            <div id="${this.fieldId}-loading-container" class="file-upload-loading">
                <div class="font-size14 mb-1">Loading</div>
                <div class="">
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        `

        let fileErrorBlock = `
            <div class="my-2 error-block d-none" id="${this.fieldId}-error-block">
                <div>
                    <span class="pr-2 error-icon"><i class="fa-solid fa-circle-xmark fa-lg"></i></span>
                    <span class="error-text"></span>
                </div>
            </div>
        `

        let displayFile = "File";
        if (this.options.isPdf) {
            displayFile = "PDF";
        }
        if (this.options.isMultiple) {
            displayFile += "s";
        }

        let fileUploadBlock = `
            <div id="${this.fieldId}-file-upload-block" class="file-upload-block">
                <div class="text-center">
                    <i class="fas fa-cloud-upload-alt fa-2x"></i>
                </div>
                <p class="text-center">Drag and Drop ${displayFile} Here!</p>

                <input id="${this.fieldId}-input" class="position-absolute file-upload-input invisible" type="file" ${this.options.isMultiple ? "multiple" : ""}/>

                <div class="text-center mb-2">
                    <label class="btn btn-sm choose-file-btn" for="${this.fieldId}-input">Choose ${displayFile} <i class="fa-solid fa-file pl-1"></i></label>
                </div>
            </div>          
        `;

        let fileGalleryBlock = `
            <div id="${this.fieldId}-gallery" class="file-upload-gallery"></div>
        `

        let html = `
            ${loadingBlock}
            ${fileErrorBlock}
            ${fileUploadBlock}
            ${fileGalleryBlock}
        `

        let fileContainer = $(`#${this.fieldId}`);
        fileContainer.empty().html(html);
    }

    generateHtml(): string {
        let requiredClass = "";
        if (this.isRequired) requiredClass = "required";

        let html = `
            <label class="label-title ${requiredClass}">${this.displayName}</label>
            ${this.description != "" ? `<p class='form-text'>${this.description}</p>` : ``}
            <div id="${this.fileContainerId}" class="file-upload-container">
            </div>
        `;

        return html;
    }

    initialize() {
        this.setHtml();

        this.storedValues = [];
        this.filesToUpload = [];
        this.currentDocuments = [];

        this.initializeFileUpload();
    }

    prepopulate(items: any[]): void {
        if (items.length > 1) {
            this.isMultiple = true;
        }
        else {
            this.isMultiple = false;
        }

        this.initialVal = this.defaultVal;
        if (!this.isMultiple && items.length > 0) {
            this.initialVal = _.get(items[0], this.propertyName);
        }

        if (this.initialVal) {
            this.storedValues = (<string>this.initialVal).split("|");
        }
        else {
            this.storedValues = [];
        }

        if (this.storedValues.length > 0) {
            this.refreshDocuments().then(() => {
                this.updateDocumentDisplay();
            });
        }
        else {
            this.updateDocumentDisplay();
        }
    }

    updateItem(item: any): any {
        let updatedItem = _.cloneDeep(item);
        _.set(updatedItem, this.propertyName, this.getValue())
        return updatedItem;
    }

    setOptions(pickLists: JsonItem[]) {
        return;
    }

    private initializeFileUpload(): void {
        // disable default behavior for upload blocks
        let uploadBlock = $(this.fileUploadBlockId)

        uploadBlock.on("dragenter dragover dragleave drop", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });

        uploadBlock.on("dragenter dragover", (e) => {
            uploadBlock.addClass("highlight-upload-block");
        })

        uploadBlock.on("dragleave drop", (e) => {
            uploadBlock.removeClass("highlight-upload-block");
        })

        uploadBlock.on("drop", async (e) => {
            this.filesToUpload = [];

            let uploadedFiles = e.originalEvent.dataTransfer.files;

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < 1; i++) {
                this.filesToUpload.push(uploadedFiles[i])
            }

            // Validate and Upload
            let isValidUpload = this.validateUpload();
            if (isValidUpload) {
                this.showLoading(true);
                await this.uploadDocuments();
                await this.refreshDocuments();
                this.updateDocumentDisplay(true);
            }
        })

        let uploadInput = $(this.fileUploadInputId);

        uploadInput.on("change", async (e) => {
            this.filesToUpload = [];

            let uploadedFiles: File[] = uploadInput.prop("files");

            if (uploadedFiles.length <= 0) {
                return;
            }

            for (let i = 0; i < uploadedFiles.length; i++) {
                this.filesToUpload.push(uploadedFiles[i])
            }

            // Validate and Upload
            let isValidUpload = this.validateUpload();
            if (isValidUpload) {
                this.showLoading(true);
                await this.uploadDocuments();
                await this.refreshDocuments();
                this.updateDocumentDisplay(true);
            }
        });
    }

    private async refreshDocuments() {
        this.filesToUpload = [];
        this.currentDocuments = [];
        if (this.storedValues.length > 0) {
            let result = await this.getDocuments();
            if (result["items"]) {
                this.currentDocuments = plainToClass(DocumentInfoV2, <DocumentInfoV2[]>result["items"]);
            }
        }
    }

    private showLoading(isLoading: boolean) {
        if (isLoading) {
            $(this.fileUploadBlockId).addClass('d-none');
            $(this.fileGalleryId).addClass('d-none');
            $(this.fileLoadingId).removeClass('d-none');
        }
        else {
            $(this.fileLoadingId).addClass('d-none');
        }
    }

    private async getDocuments() {
        this.currentDocuments = [];
        if (this.storedValues.length == 0) {
            return "[]"; // If nothing stored return nothing
        }

        this.showLoading(true);

        let result: any;

        let requestBody = {
            rootFolderPath: this.options.rootFolderPath,
            uuidArray: this.storedValues
        }

        try {
            result = await $.ajax(this.getFilesRoute, {
                method: "POST",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                data: JSON.stringify(requestBody),
                contentType: "application/json; charset=utf-8"
            });

            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    private drawFileGallery(triggerChange: boolean = false) {
        $(this.fileGalleryId).empty();

        let content = "";
        // Add Content for Each File
        for (let i = 0; i < this.currentDocuments.length; i++) {
            let doc = this.currentDocuments[i];
            // Add logic to determine UUID
            let folderPathArr = doc.id.split(`${this.options.rootFolderPath}/`);
            let filePath = folderPathArr.pop();
            if (filePath) {
                let filePathArr = filePath.split("/");
                let uuid = filePathArr.shift();
                if (uuid) {
                    let icon = `<i class="${doc.icon}"></i>`

                    let viewBtn = "";
                    if (this.options.isPdf) {
                        viewBtn = `
                            <button class="butn px-3 py-2 mr-2 file-upload-view-btn" data-uuid="${uuid}"><span><i class="fa-solid fa-magnifying-glass"></i></span></button>
                        `
                    };

                    let encodedId = encodeURI(doc.id);
                    let downloadBtn = `
                        <a href="${this.downloadFileRoute}?filePath=${encodedId}" class="butn px-3 py-2 mr-2 file-upload-download-btn" download="${doc.text}"><span><i class="fa-solid fa-file-arrow-down"></i></span></a>
                    `;

                    let deleteBtn = `
                        <button class="butn px-3 py-2 mr-2 file-upload-delete-btn" data-uuid="${uuid}"><span><i class="fa-solid fa-trash-can"></i></span></button>
                    `;

                    let btnGallery = `
                        <div class="d-flex">
                            ${viewBtn}
                            ${downloadBtn}
                            ${deleteBtn}
                        </div>
                    `

                    let galleryItem = `
                        <div class="gallery-item">
                            <div class="col-12">
                                <div class="header-text">
                                    <div class="text-decoration-none d-flex align-items-center">
                                        <div class="pr-3">
                                            ${icon}
                                        </div>
                                        <div class="expand-text pr-3">
                                            ${doc.text}
                                        </div>
                                        <div class="ml-auto">
                                            ${btnGallery}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    `;

                    content += galleryItem;
                }              
            }
        }

        $(this.fileGalleryId).append(content);
        let fileUploadField = this;

        $(this.fileGalleryId).find(".file-upload-delete-btn").on('click', async function () {
            let uuid: string = $(this).data("uuid");
            if (uuid) {
                let foundIndex = fileUploadField.storedValues.findIndex(x => x == uuid);
                if (foundIndex >= 0) {
                    fileUploadField.showLoading(true);
                    fileUploadField.storedValues.splice(foundIndex, 1);
                    await fileUploadField.refreshDocuments();
                    fileUploadField.updateDocumentDisplay(true);
                }
            }
        });

        $(this.fileGalleryId).find(".file-upload-view-btn").on('click', function () {
            let uuid: string = $(this).data("uuid");
            if (uuid) {
                let foundDoc = fileUploadField.currentDocuments.find(x => x.id.includes(uuid));
                if (foundDoc) {
                    fileUploadField.viewPdf(foundDoc);
                }
            }
        });

        if (triggerChange) {
            $(`#${this.fieldId}`).trigger("form-field-change")
        }
    }

    private updateDocumentDisplay(triggerChange: boolean = false) {
        this.drawFileGallery(triggerChange); // Updates Gallery

        // if multiple, always allow for uploads
        if (this.options.isMultiple) {
            $(this.fileUploadBlockId).removeClass("d-none");
        }
        else {
            // if only one upload, see if there is an existing file
            if (this.currentDocuments.length > 0) {
                $(this.fileUploadBlockId).addClass("d-none");
            }
            else {
                $(this.fileUploadBlockId).removeClass("d-none");
            }
        }

        if (this.currentDocuments.length > 0) {
            $(this.fileGalleryId).removeClass("d-none");
        }

        $(this.fileLoadingId).addClass("d-none");
    }

    private async uploadDocuments() {
       
        if (this.filesToUpload.length > 0) {

            for (let i = 0; i < this.filesToUpload.length; i++) {
                let file = this.filesToUpload[i];
                let formData: FormData = new FormData();
                let uuid = crypto.randomUUID();
                formData.append(uuid, file); 

                let result: any;
                try {
                    result = await $.ajax(this.addFilesRoute, {
                        method: "POST",
                        headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val(), "RootFolderPath": this.options.rootFolderPath, "UUID": uuid },
                        contentType: false,
                        cache: false,
                        processData: false,
                        data: formData
                    });

                    this.storedValues.push(uuid);
                }
                catch (error) {
                    alert("An error has occurred, please refresh the page and try again.");
                    console.log(error);
                }
            }   
        }
    }

    getValue(): any {
        return this.storedValues.join("|");
    }

    setValue(formData: any) {
        let pipePathIds = <string>_.get(formData, this.propertyName);
        if (pipePathIds) {
            this.storedValues = pipePathIds.split("|");
        }
        else {
            this.storedValues = [];
        }
    }

    private setErrorText(errorText: string = null) {
        if (errorText == null) {
            $(this.fileErrorBlock).text('');
            $(this.fileErrorBlock).addClass('d-none');
        }
        else {
            $(this.fileErrorBlock).text(errorText);
            $(this.fileErrorBlock).removeClass('d-none');
        }
    }

    private validateUpload(): boolean {
        if (this.options.isPdf) {
            if (this.filesToUpload.some(file => !file.type.includes("pdf"))) {
                this.setErrorText("Please select a PDF document.")
                return false;
            }
        }

        if (!this.options.isMultiple) {
            if (this.storedValues.length > 0) {
                this.setErrorText("Max Upload size is 1 file");
                return false;
            }
        }

        let totalSize = 0;
        this.filesToUpload.forEach(file => {
            totalSize += file.size;
        });
        if (totalSize > this.maxFileSize) {
            this.setErrorText("Uploaded file exceeds max file size");
            return false;
        }

        this.setErrorText();
        return true;
    }

    private viewPdf(doc: DocumentInfoV2) {
        let foundModal = $("#view-document-modal");
        if (foundModal.length > 0) {
            foundModal.find("#filename").text(doc.text);
            foundModal.find("#document-iframe").attr("src", doc.link);
            foundModal.modal("show");
        }
    }

    checkValid(): boolean {
        if (this.isRequired) {
            // empty or spaces
            if (this.storedValues.length == 0) {
                return false;
            }
            return true;
        }
        else {
            return true;
        }
    }

    checkChanged(): boolean {
        let val = this.getValue();
        if (val != this.initialVal) {
            return true;
        }
        return false;
    }

    displayValid(isValid: boolean) {
        if (isValid) {
            $(`#${this.fieldId}`).addClass("is-valid").removeClass("is-invalid");
        }
        else {
            $(`#${this.fieldId}`).removeClass("is-valid").addClass("is-invalid");
        }
    }

    resetValidDisplay() {
        $(`#${this.fieldId}`).removeClass("is-valid").removeClass("is-invalid");
    }

    generateChangeHtml(newItem: any, prevItem: any): string {
        return "";
    }

    compareItems(item1: any, item2: any): boolean {
        return true;
    }
}

